import { Dispatch, SetStateAction } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Box, Stack } from '@mui/system';
import { CompanyDto } from 'tdc-web-backend/companies/schemas';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../../../../../../../../../components/confirm-modal/ConfirmModal';
import { ReactComponent as SuitcaseCheckIcon } from '../../../../../../../../../../assets/icons/layout-icons/SuitcaseCheckIcon.svg';
import { primaryLight } from '../../../../../../../../../../utils/color';
import { useRefresh } from '../../../../../../../../../../utils/hooks/crud-hooks/useRefresh';
import CustomInput from '../../../../../../../../../../components/custom-input/CustomInput';
import { SocialIconMapping } from '../CompanySocialMedia';
import useUserCompany from '../../../../../../../../../../utils/hooks/useUserCompany';

interface CompanySocialMediaEditModalProps {
  isEditModalOpen: boolean;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
  socialIcons: SocialIconMapping;
}

const CompanySocialMediaEditModal = ({
  isEditModalOpen,

  setIsEditModalOpen,
  socialIcons,
}: CompanySocialMediaEditModalProps) => {
  const { company: companyData, updateCompany, isLoadingUpdateCompany } = useUserCompany();
  const refresh = useRefresh();
  const formMethods = useForm<Pick<CompanyDto, 'socialMediaLinks'>>({
    defaultValues: {
      socialMediaLinks: {
        medium: companyData?.socialMediaLinks?.medium ?? '',
        dribbble: companyData?.socialMediaLinks?.dribbble ?? '',
        facebook: companyData?.socialMediaLinks?.facebook ?? '',
        github: companyData?.socialMediaLinks?.github ?? '',
        instagram: companyData?.socialMediaLinks?.instagram ?? '',
        linkedin: companyData?.socialMediaLinks?.linkedin ?? '',
        twitter: companyData?.socialMediaLinks?.twitter ?? '',
      },
    },
    mode: 'onChange',
  });
  const { t } = useTranslation();
  const onSubmit = (data: Pick<CompanyDto, 'socialMediaLinks'>) => {
    updateCompany(data, {
      onSuccess: () => {
        refresh();
        setIsEditModalOpen(false);
      },
    });
  };

  const url = /^(http|https):\/\/[^ "]+$/;

  return (
    <ConfirmModal
      width="40%"
      isModalOpen={isEditModalOpen}
      setIsModalOpen={setIsEditModalOpen}
      title={t('settings.companyProfile.socialMedia')}
      onCancel={() => setIsEditModalOpen(false)}
      automaticallyCloseModalOnButtonClick={false}
      isLoading={isLoadingUpdateCompany}
      onConfirm={formMethods.handleSubmit(onSubmit)}
      confirmButtonText={t('common.save')}
      message={
        <FormProvider {...formMethods}>
          <Box component="form" sx={{ maxHeight: '398px', overflowY: 'auto' }}>
            {Object.entries(socialIcons).map(([platform, key]) => {
              const IconComponent = socialIcons[platform.toLowerCase()];
              return (
                <Stack
                  key={platform}
                  spacing={1}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mr="10px"
                  mt="14px"
                >
                  <CustomInput
                    input={<CustomInput.Text />}
                    placeholder={t('common.type')}
                    name={`socialMediaLinks.${platform}`}
                    label={platform as string}
                    sx={{ width: '100%', minHeight: 'none', mb: '10px' }}
                    adornment={{ icon: <IconComponent /> }}
                    defaultValue={key}
                    rules={{
                      pattern: {
                        value: url,
                        message: t('errors.invalidUrl'),
                      },
                    }}
                  />
                </Stack>
              );
            })}
          </Box>
        </FormProvider>
      }
      iconProps={{
        icon: <SuitcaseCheckIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: primaryLight[100],
      }}
    />
  );
};

export default CompanySocialMediaEditModal;
